/**
 * ZeoSidebar - Chat-Historie (aehnlich ChatGPT): Suche, Gruppierung nach
 * Tag, Scope-Badge pro Chat ("Büro" oder Klientenname).
 *
 * WICHTIG: ein Klick auf einen bestehenden Chat uebergibt dessen EIGENEN
 * kontext_typ/kontext_id an onSelectSession -- der Scope eines Chats wird
 * hier NIE umgeschrieben, nur angezeigt/weitergereicht (Cross-Context-Schutz
 * bleibt serverseitig ohnehin bestehen, s. assistent_routes.py::chat()).
 */
import { useEffect, useMemo, useState } from 'react';
import { Search, Plus, Pencil } from 'lucide-react';
import ZeoIcon from '@/components/ZeoIcon';
import { filtereSessions, snippetFuer } from './zeoWorkspace';

function gruppenSchluessel(datumStr) {
  if (!datumStr) return 'Älter';
  const datum = new Date(datumStr);
  const heute = new Date();
  const start = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diffTage = Math.round((start(heute) - start(datum)) / 86400000);
  if (diffTage <= 0) return 'Heute';
  if (diffTage === 1) return 'Gestern';
  if (diffTage <= 7) return 'Letzte 7 Tage';
  return 'Älter';
}

const GRUPPEN_REIHENFOLGE = ['Heute', 'Gestern', 'Letzte 7 Tage', 'Älter'];

export default function ZeoSidebar({
  open, sessions, activeSessionId, onSelectSession, onNewChat, klientNamenCache,
  aktiverKlientId = null, onRename = null, onSuche = null,
}) {
  const [suche, setSuche] = useState('');
  const [nurKlient, setNurKlient] = useState(true);
  const [inhaltTreffer, setInhaltTreffer] = useState([]);
  const [editId, setEditId] = useState(null);
  const [editText, setEditText] = useState('');
  // Scope der Suche: ist der aktive Chat an einen Klienten gebunden, sucht die Suche standardmaessig NUR in dessen Chats.
  const scopeKlientId = nurKlient ? aktiverKlientId : null;

  useEffect(() => {
    const q = suche.trim();
    if (!onSuche || q.length < 2) { setInhaltTreffer([]); return undefined; }
    let abgebrochen = false;
    const t = setTimeout(async () => {
      const treffer = await onSuche(q, scopeKlientId);
      if (!abgebrochen) setInhaltTreffer(Array.isArray(treffer) ? treffer : []);
    }, 300);
    return () => { abgebrochen = true; clearTimeout(t); };
  }, [suche, scopeKlientId, onSuche]);

  const speichereTitel = async (session) => {
    const neu = editText.trim();
    setEditId(null);
    if (neu && neu !== session.titel && onRename) await onRename(session.id, neu);
  };

  const scopeBadge = (session) => {
    if (session.kontext_typ === 'betreuungsakte' && session.kontext_id) {
      return klientNamenCache[session.kontext_id] || 'Klient';
    }
    return 'Büro';
  };

  const gefiltert = useMemo(
    () => filtereSessions(sessions, suche, inhaltTreffer, scopeKlientId, scopeBadge),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sessions, suche, inhaltTreffer, scopeKlientId, klientNamenCache],
  );

  const gruppiert = useMemo(() => {
    const gruppen = { Heute: [], Gestern: [], 'Letzte 7 Tage': [], Älter: [] };
    for (const s of gefiltert) {
      gruppen[gruppenSchluessel(s.updated_date)].push(s);
    }
    return gruppen;
  }, [gefiltert]);

  if (!open) return null;

  return (
    <div className="w-72 shrink-0 h-full flex flex-col bg-white border-r border-violet-100" data-testid="zeo-sidebar">
      <div className="p-4 flex items-center gap-2 border-b border-violet-50">
        <div className="w-8 h-8 rounded-full bg-violet-50 flex items-center justify-center">
          <ZeoIcon size={22} />
        </div>
        <span className="font-semibold text-slate-800">ZEO</span>
      </div>

      <div className="p-3">
        <button
          type="button"
          onClick={onNewChat}
          className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl bg-violet-600 text-white text-sm font-medium hover:bg-violet-700 transition-colors shadow-sm"
          data-testid="zeo-new-chat"
        >
          <Plus size={16} /> Neuer Chat
        </button>
      </div>

      <div className="px-3 pb-2">
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={suche}
            onChange={(e) => setSuche(e.target.value)}
            placeholder="Suche"
            className="w-full pl-9 pr-3 py-2 text-sm rounded-lg bg-slate-50 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-violet-300"
            data-testid="zeo-search"
          />
        </div>
        {aktiverKlientId && suche.trim() && (
          <label className="mt-2 flex items-center gap-2 text-[11px] text-slate-500" data-testid="zeo-search-scope">
            <input type="checkbox" checked={nurKlient} onChange={(e) => setNurKlient(e.target.checked)} />
            Nur Chats dieses Klienten
          </label>
        )}
      </div>

      <div className="flex-1 overflow-y-auto px-2 pb-4">
        {GRUPPEN_REIHENFOLGE.map((gruppe) => (
          gruppiert[gruppe].length > 0 && (
            <div key={gruppe} className="mb-3">
              <div className="px-2 py-1 text-[11px] font-semibold text-slate-400 uppercase tracking-wide">
                {gruppe}
              </div>
              {gruppiert[gruppe].map((s) => (
                <div key={s.id} className="group relative">
                  {editId === s.id ? (
                    <input
                      autoFocus
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      onBlur={() => speichereTitel(s)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') speichereTitel(s);
                        if (e.key === 'Escape') setEditId(null);
                      }}
                      className="w-full px-3 py-2 text-sm rounded-lg border border-violet-300 focus:outline-none focus:ring-2 focus:ring-violet-300"
                      data-testid="zeo-session-rename-input"
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => onSelectSession(s)}
                      onDoubleClick={() => { if (onRename) { setEditId(s.id); setEditText(s.titel || ''); } }}
                      className={`w-full text-left px-3 py-2 rounded-lg mb-0.5 transition-colors ${
                        s.id === activeSessionId ? 'bg-violet-50' : 'hover:bg-slate-50'
                      }`}
                      data-testid="zeo-session-item"
                    >
                      <div className="text-sm text-slate-700 truncate pr-6">{s.titel || 'Ohne Titel'}</div>
                      <div className="text-[11px] text-violet-500 mt-0.5">{scopeBadge(s)}</div>
                      {snippetFuer(inhaltTreffer, s.id) && (
                        <div className="text-[11px] text-slate-400 mt-0.5 truncate" data-testid="zeo-session-snippet">… {snippetFuer(inhaltTreffer, s.id)}</div>
                      )}
                    </button>
                  )}
                  {onRename && editId !== s.id && (
                    <button
                      type="button"
                      title="Chat umbenennen"
                      onClick={() => { setEditId(s.id); setEditText(s.titel || ''); }}
                      className="absolute right-2 top-2 p-1 rounded text-slate-300 hover:text-violet-600 opacity-0 group-hover:opacity-100 focus:opacity-100"
                      data-testid="zeo-session-rename"
                    >
                      <Pencil size={13} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )
        ))}
        {gefiltert.length === 0 && (
          <div className="px-3 py-6 text-sm text-slate-400 text-center">Keine Chats gefunden.</div>
        )}
      </div>
    </div>
  );
}
